import ABCyaPhaser from '@abcya/abcya_phaser';
import ABCyaLib from '@abcya/abcya_lib';
import Phaser from 'phaser';
import ShapeBackground from '../common/ShapeBackground';
import SpritePool from '../common/SpritePool';
import { arrDiff, getPrimes } from '../common/Utilities';
import gameData, { GameVersions, GAME_IMAGES_KEY, ODD_GAME_MODE, SPARKLE_ANIMATION_KEY } from '../gameData';
import GameOverOverlay from '../ninjas/GameOverOverlay';
import BadHitParticle from '../ninjas/HitParticles/BadHitParticle';
import HitParticle from '../ninjas/HitParticles/HitParticle';
import HudDisplay from '../ninjas/HudDisplay';
import MouseEmitter from '../ninjas/MouseParticles/MouseEmitter';
import PlusScore from '../ninjas/PlusScore';
import TossItem from '../ninjas/TossItem';
import TossItemHalf from '../ninjas/TossItemHalf';
import SoundAssetKeys from '../SoundAssetKeys';
import UIAssetKeys from '../UIAssetKeys';
import IntroScreenBase from './IntroScreenBase';

export default class Game extends Phaser.Scene {
  static get KEY() { return 'Game'; }

  init() {
    this.wrongAlertFlash = null;
    this.hudDisplay = null;

    this.mouseMoveListener = null;
    this.mouseTrail = null;


    /** Items * */
    this.itemPool = null;
    this.activeItems = null;
    this.goodItems = null;
    this.badItems = null;
    this.factorListing = null;

    this.itemHalfPool = null;
    this.activeItemHalves = null;

    this.itemHitParticlePool = null;
    this.itemHitBadParticlePool = null;
    this.activeItemHitParticles = null;

    /** @type {BadHitParticle[]} */
    this.activeBadParticles = null;

    this.pointDisplayPool = null;
    this.activePointDisplays = null;

    this.throwWaiter = null;
    this.lastThrowTime = null;

    this.badThrowWaiter = null;
    this.lastBadThrowTime = null;

    this.sparkleFrameCount = null;


    // //
    this.bgMusic = null;
    this.gameType = null;
    this.currentSwitch = null;
    this.spritePrefix = null;

    this.isGameOver = null;

    /** @type {Phaser.Time.TimerEvent} */
    this.timer = null;

    this.isSwiping = false;

    this.gameOverPopup = null;

    // Runtime
    this.curScore = null;
    this.wrongCount = null;
    this.totalThrown = 0;
    this.totalHit = 0;
    this.missedItemCount = 0;

    this.accuracy = null;

    // Combos
    this.comboCount = 0;
    this.timeOfLastHit = 0;
    this.totalCombos = 0;
  }

  create() {
    this.bg = ABCyaPhaser.Images.addImage(0,0, 'MainBackground', this, GAME_IMAGES_KEY).setOrigin(0);

    this.goodItems = [];
    this.badItems = [];
    this.curScore = 0;
    this.totalThrown = 0;
    this.totalHit = 0;
    this.missedItemCount = 0;
    this.wrongCount = 0;

    this.comboCount = 0;
    this.timeOfLastHit = 0;
    this.totalCombos = 0;

    this.spritePrefix = 'Num';

    this.isGameOver = false;

    this.sparkleFrameCount = this.textures.get(SPARKLE_ANIMATION_KEY).frameTotal - 2;

    this.throwWaiter = 1110;
    this.lastThrowTime = this.time.now;

    this.badThrowWaiter = 4450;
    this.lastBadThrowTime = this.time.now;

    this.itemPool = new SpritePool(TossItem, 25, this);

    /** @type {TossItem[]} */
    this.activeItems = [];

    this.itemHalfPool = new SpritePool(TossItemHalf, 150, this);

    /** @type {TossItemHalf[]} */
    this.activeItemHalves = [];

    this.itemHitParticlePool = new SpritePool(HitParticle, 250, this);
    this.itemHitBadParticlePool = new SpritePool(BadHitParticle, 250, this);

    /** @type {HitParticle[]} */
    this.activeItemHitParticles = [];
    this.activeBadParticles = [];

    this.pointDisplayPool = new SpritePool(PlusScore, 8, this);
    this.activePointDisplays = [];

    this.mouseTrail = new MouseEmitter(this);
    this.mouseMoveListener = this.input.on(Phaser.Input.Events.POINTER_MOVE, this.handleMouseMove, this);
    this.add.existing(this.mouseTrail);

    this.hudDisplay = new HudDisplay(this);
    this.hudDisplay.on('TIMES_UP_COMPLETE', this.timesUpAnimationDone, this);
    this.add.existing(this.hudDisplay);

    this.wrongAlertFlash = new ShapeBackground(this, this.scale.width, this.scale.height, 0xff0000);
    this.wrongAlertFlash.alpha = 0;
    this.add.existing(this.wrongAlertFlash);

    this.gameType = gameData.gameVersion;
    switch (this.gameType) {
      case GameVersions.OddOrEven:
        this.oddEvenSwitch(gameData.gameMode === ODD_GAME_MODE);
        break;

      case GameVersions.Factors:
        this.factorSwitch(this.gameType);
        break;

      case GameVersions.Multiples:
        this.multiplesSwitch(gameData.gameMode);
        break;
    }

    this.gameOverPopup = new GameOverOverlay(this);
    this.gameOverPopup.x = 0;
    this.gameOverPopup.y = 20;
    this.gameOverPopup.on('PLAY_AGAIN_CLICK', this.restartGame, this);
    this.add.existing(this.gameOverPopup.setVisible(false));

    if (!ABCyaLib.DEVICE_INFO.desktop) {
      this.isSwiping = false;
      this.bg.setInteractive();
      this.bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.startSlicing, this);
      this.bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, this.stopSlicing, this);
    }
    else {
      this.isSwiping = true;
    }
    this.startTimer();
  }

  /**
   * 
   * @param {PlusScore} item 
   */
  resetFloatDisplay (item) {
    item.setVisible(false);
    this.pointDisplayPool.returnSprite(item);
    const index = this.activePointDisplays.indexOf(item);
    this.activePointDisplays.splice(index,1);
  }

  handleMouseMove (pointer) {
    this.mouseTrail.makeParticle(pointer.x, pointer.y);
  }

  timesUpAnimationDone () {
    this.hudDisplay.hideTimesUp();
    this.doGameOver();
  }

  doGameOver (showRed) {
    this.pauseTimer();
    gameData.bestScore = Math.max(this.curScore, gameData.bestScore);
    gameData.gameStorage.setValue('bestScore', gameData.bestScore.toString());

    this.gameOverPopup.setScores(this.curScore, this.accuracy);
    this.accuracy = null;
    this.isGameOver = true;

    this.gameOverPopup.alpha = 0;
    this.tweens.killTweensOf(this.gameOverPopup);
    ABCyaPhaser.Tweens.addTween(this.gameOverPopup, 1300, { delay: 400, alpha: 1 }, Phaser.Math.Easing.Quadratic.Out);
    this.hudDisplay.fadeOut(300);

    this.children.bringToTop(this.gameOverPopup.setVisible(true));

    if (showRed === true) {
      this.wrongAlertFlash.alpha = 1;
      this.tweens.killTweensOf(this.wrongAlertFlash);
      ABCyaPhaser.Tweens.addTween(this.wrongAlertFlash, 1810, { alpha: 0 }, Phaser.Math.Easing.Quadratic.In);

      this.children.bringToTop(this.wrongAlertFlash.setVisible(true));
    }
  }

  pauseTimer () {
    if (this.timer) this.timer.remove();
    this.timer = null;
  }

  calculateAccuracy () {
    // Subtract any current good items from total
    let activeCount = 0;
    const len = this.activeItems.length;
    for (let i = 0; i < len; i++) {
      if (this.activeItems[i].isGood) activeCount++;
    }
    const badCount = this.missedItemCount + this.wrongCount;
    const hitcount = this.totalHit - badCount;
    const totalToInclude = this.totalThrown - activeCount;
    let accuracy = 0;
    if (hitcount > 0) {
      accuracy = Math.floor((hitcount / totalToInclude) * 100);
    }
    this.accuracy = accuracy;
  }

  oddEvenSwitch (isOdd) {
    isOdd ? this.currentSwitch = 'ODD' : this.currentSwitch = 'EVEN';
    this.hudDisplay.updateLabel(this.currentSwitch);

    /** @type {number[]} */
    this.goodItems = [];

    /** @type {number[]} */
    this.badItems = [];
    for (let i = 1; i <= gameData.MAX_NUM_VALUE; i++) {
      if (isOdd) {
        if (i % 2) {
          this.goodItems.push(i);
        }
        else {
          this.badItems.push(i);
        }
      }
      else {
        if (i % 2) {
          this.badItems.push(i);
        }
        else {
          this.goodItems.push(i);
        }
      }
    }
  }

  factorSwitch (value) {
    this.currentSwitch = value;
    this.hudDisplay.updateLabel(this.currentSwitch);
    this.badItems = getPrimes(gameData.MAX_NUM_VALUE);
    const allnums = [];
    for (let i = 2; i <= gameData.MAX_NUM_VALUE; i++) {
      allnums.push(i);
    }
    this.goodItems = arrDiff(allnums, this.badItems);
    this.factorListing = []; // This will line up with the good items array
    const len = this.goodItems.length;
    for (let j = 0; j < len; j++) {
      // Find all the numbers divisible
      const divisibleby = [];
      for (let k = 2; k < this.goodItems[j]; k++) {
        if (this.goodItems[j] % k === 0) {
          divisibleby.push(k);
        }
      }
      this.factorListing.push(divisibleby);
    }
  }

  multiplesSwitch (num) {
    this.currentSwitch = num;
    const prefix = ABCyaLib.Environment.instance.gameConfig.gameScreenTopTextPrefix;
    this.hudDisplay.updateLabel(`${prefix} ${this.currentSwitch.toString()}`);
    this.goodItems = [];
    this.badItems = [];
    let spriteref = '';
    for (let i = 1; i <= gameData.MAX_NUM_VALUE; i++) {
      spriteref = this.textures.getFrame(GAME_IMAGES_KEY, `Num${i}`);
      if (!spriteref) {
        continue;
      }
      if (i % num === 0) {
        this.goodItems.push(i);
      }
      else {
        this.badItems.push(i);
      }
    }
  }

  restartGame () {
    if (ABCyaLib.Environment.instance.gameConfig.showIntroScreenEachPlaythrough) {
      // go back to the intro screen
      this.scene.start(IntroScreenBase.KEY);
      return;
    }

    this.gameOverPopup.setVisible(false);

    this.curScore = 0;
    this.wrongCount = 0;

    this.totalThrown = 0;
    this.totalHit = 0;
    this.missedItemCount = 0;

    this.comboCount = 0;
    this.timeOfLastHit = 0;
    this.totalCombos = 0;
    this.hudDisplay.reset();
    switch (this.gameType) {
      case GameVersions.OddOrEven:
        this.oddEvenSwitch(gameData.gameMode === ODD_GAME_MODE);
        break;

      case GameVersions.Factors:
        // don't need to reset factors
        break;

      case GameVersions.Multiples:
        this.multiplesSwitch(gameData.gameMode);
        break;
    }

    this.isGameOver = false;
    this.startTimer();
  }

  startTimer () {
    this.timeleft = gameData.LEVEL_TIME;
    this.hudDisplay.setTimeLeft(this.timeleft);
    if (this.timer) this.timer.remove();
    this.timer = this.time.addEvent({ delay: 1000, loop: true, callback: this.timerTick, callbackScope: this });
  }

  timerTick () {
    this.timeleft--;
    if (this.timeleft === 0) {
      this.timesUp();
    }
    this.hudDisplay.setTimeLeft(this.timeleft);
  }

  startSlicing () {
    this.isSwiping = true;
  }

  stopSlicing () {
    this.isSwiping = false;
  }

  timesUp () {
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._ninja_theme, this);
    this.isGameOver = true;
    this.timer.remove();
    this.timer = null;

    this.calculateAccuracy();
    this.hudDisplay.showTimesUp();
  }

  resetItem (i) {
    this.activeItems[i].setVisible(false);
    this.itemPool.returnSprite(this.activeItems[i]);
    this.activeItems.splice(i,1);
  }

  createBadHitParticles (x,y) {
    const count = 12;
    for (let i = 0; i < count; i++) {
      /** @type {BadHitParticle} */
      const item = this.itemHitBadParticlePool.getSprite();
      item.reset(x,y);
      this.add.existing(item.setVisible(true));
      this.children.bringToTop(item);
      this.activeBadParticles.push(item);
    }
  }

  checkHits (x,y,r1,r2,r3,r4) {
    const x1 = Math.min(r1, r3);
    const x2 = Math.max(r1, r3);
    const y1 = Math.min(r2, r4);
    const y2 = Math.max(r2, r4);
    return (x1 <= x) && (x <= x2) && (y1 <= y) && (y <= y2);
  }

  /**
   * 
   * @param {TossItem} item 
   */
  hitGoodItem (item) {
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._correct_slice, this);
    this.totalHit++;
    this.checkForCombo(item.x,item.y);
    const points = item.scoreValue * (this.comboCount + 1);
    this.createFloatDisplay(item.x, item.y, `plus${points}`);
    this.curScore += points;
    this.hudDisplay.updateScore(this.curScore);
    if (this.gameType === GameVersions.Factors) {
      this.checkForFactorsOnHit(item);
    }
  }

  /**
   * 
   * @param {TossItem} item 
   */
  checkForFactorsOnHit (item) {
    const fIndex = this.goodItems.indexOf(item.num);
    const factor1 = this.factorListing[fIndex][Phaser.Math.Between(0,this.factorListing[fIndex].length - 1)];
    const factor2 = item.num / factor1;
    this.throwSpecificItem(factor1, item.x, item.y - 20,0);
    this.throwSpecificItem(factor2, item.x, item.y - 20,1);
  }

  throwSpecificItem (itemNum, startX, startY,direction) {
    if (this.isGameOver) return;

    /** @type {TossItem} */
    const item = this.itemPool.getSprite();
    const value = itemNum;
    const sn = this.spritePrefix + value;

    const isGood = this.goodItems.indexOf(value) >= 0;
    if (isGood) {
      this.totalThrown++;
    }
    item.resetForHitSpawn(sn, 20, isGood, value, startX, startY, direction);

    this.add.existing(item.setVisible(true));
    this.children.bringToTop(item);
    this.activeItems.push(item);
  }

  checkForCombo (x,y) {
    const curTime = this.time.now;
    if (curTime - this.timeOfLastHit < COMBO_TIME_LIMIT) {
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._combo, this);
      this.comboCount++;
      this.totalCombos++;
      this.createFloatDisplay(x, y + 43,UIAssetKeys._ComboText, true);
    }
    else {
      this.comboCount = 0;
    }
    this.timeOfLastHit = curTime;
  }

  createFloatDisplay (x,y,sn, doscale) {
    /** @type {PlusScore} */
    const item = this.pointDisplayPool.getSprite();
    item.reset(x,y,sn, doscale);
    this.add.existing(item.setVisible(true));
    this.children.bringToTop(item);
    this.activePointDisplays.push(item);
  }

  createHitParticles (x,y) {
    const count = 50;
    for (let i = 0; i < count; i++) {
      /** @type {HitParticle} */
      const item = this.itemHitParticlePool.getSprite();
      item.reset(x,y,Phaser.Math.Between(0,this.sparkleFrameCount));
      this.add.existing(item.setVisible(true));
      this.children.bringToTop(item);
      this.activeItemHitParticles.push(item);
    }
  }

  createHalves (itemName, x, y) {
    /** @type {TossItemHalf} */
    let item = this.itemHalfPool.getSprite();
    item.reset(itemName, 1, x, y);
    this.add.existing(item.setVisible(true));
    this.children.bringToTop(item);
    this.activeItemHalves.push(item);

    item = this.itemHalfPool.getSprite();
    item.reset(itemName, 2, x, y);
    this.add.existing(item.setVisible(true));
    this.children.bringToTop(item);
    this.activeItemHalves.push(item);
  }

  hitBadItem () {
    this.wrongAlertFlash.alpha = 1;
    this.tweens.killTweensOf(this.wrongAlertFlash);
    ABCyaPhaser.Tweens.addTween(this.wrongAlertFlash, 100, { alpha: 0 }, Phaser.Math.Easing.Linear);
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._wrong_slice, this);
  }

  resetHalf (i) {
    this.activeItemHalves[i].setVisible(false);
    this.itemHalfPool.returnSprite(this.activeItemHalves[i]);
    this.activeItemHalves.splice(i,1);
  }

  resetHitParticle (i) {
    this.activeItemHitParticles[i].setVisible(false);
    this.itemHitParticlePool.returnSprite(this.activeItemHitParticles[i]);
    this.activeItemHitParticles.splice(i,1);
  }

  resetBadHitParticle (i) {
    this.activeBadParticles[i].setVisible(false);
    this.itemHitBadParticlePool.returnSprite(this.activeBadParticles[i]);
    this.activeBadParticles.splice(i,1);
  }

  throwItem () {
    if (this.isGameOver) return;
    const snd = `object_tossed_${Phaser.Math.Between(1,3)}`;
    ABCyaPhaser.Audio.playOneShot(snd, this);

    /** @type {TossItem} */
    const item = this.itemPool.getSprite();
    const num = Phaser.Math.Between(0,this.goodItems.length - 1);
    const value = this.goodItems[num];
    const sn = this.spritePrefix + value;
    item.reset(sn, 10, true, value);
    this.add.existing(item.setVisible(true));
    this.children.bringToTop(item);
    this.activeItems.push(item);
  }

  throwBadItem () {
    if (this.isGameOver) return;
    const snd = `object_tossed_${Phaser.Math.Between(1,3)}`;
    ABCyaPhaser.Audio.playOneShot(snd, this);

    /** @type {TossItem} */
    const item = this.itemPool.getSprite();
    const num = Phaser.Math.Between(0,this.badItems.length - 1);
    const value = this.badItems[num];
    const sn = this.spritePrefix + value;
    item.reset(sn, 0, false, value);
    this.add.existing(item.setVisible(true));
    this.children.bringToTop(item);
    this.activeItems.push(item);
  }

  update(time, delta) {
    let len = this.activeItems.length - 1;
    let itembounds = null;
    for (let i = len; i >= 0; i--) {
      const item = this.activeItems[i];
      if (item.y > this.scale.height + 100) {
        if (item.isGood) this.missedItemCount++;
        this.resetItem(i);
      }
      else {
        item.tick(delta);
        if (item.isDead) continue;
        if (item.invulnerable) {
          continue;
        }
        if (this.isGameOver) {
          this.createBadHitParticles(item.x, item.y);
          this.resetItem(i);
          continue;
        }
        if (!this.isSwiping) continue; // No need for hits if we are not swiping
        itembounds = item.getHitBounds();
        if (this.checkHits(this.input.activePointer.x,this.input.activePointer.y, itembounds[0], itembounds[1], itembounds[2], itembounds[3])) {

          if (item.isGood) {
            this.hitGoodItem(item);
            this.createHitParticles(item.x, item.y);

            item.gotHit();
            this.createHalves(item.spriteName, item.x, item.y);
          }
          else {
            this.hitBadItem();
            this.createBadHitParticles(item.x, item.y);
            this.wrongCount++;
            if (this.wrongCount >= maxWrong) {
              ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._game_over, this);
              this.calculateAccuracy();
              this.doGameOver(true);
            }
            else {
              this.hudDisplay.showWrong(this.wrongCount - 1);
            }
          }
          this.resetItem(i);
        }
      }
    }

    len = this.activeItemHalves.length - 1;
    for (let i = len; i >= 0; i--) {
      const item = this.activeItemHalves[i];
      if (item.y > this.scale.height + 100) {
        this.resetHalf(i);
      }
      else {
        item.tick(delta);
      }
    }

    len = this.activeItemHitParticles.length - 1;
    for (let i = len; i >= 0; i--) {
      const item = this.activeItemHitParticles[i];
      if (item.y > this.scale.height + 100) {
        this.resetHitParticle(i);
      }
      else {
        item.tick();
      }
    }

    len = this.activeBadParticles.length - 1;
    for (let i = len; i >= 0; i--) {
      const item = this.activeBadParticles[i];
      if (item.y > this.scale.height + 100) {
        this.resetBadHitParticle(i);
      }
      else {
        item.tick();
      }
    }

    if (time - this.lastThrowTime > this.throwWaiter) {
      this.throwItem();
      this.totalThrown++;

      this.throwWaiter = Phaser.Math.Between(2402,3106);
      this.lastThrowTime = time;
    }

    if (time - this.lastBadThrowTime > this.badThrowWaiter) {
      this.throwBadItem();

      this.badThrowWaiter = Phaser.Math.Between(6166,7250);
      this.lastBadThrowTime = time;
    }

    this.mouseTrail.tick();
  }
}

const maxWrong = 3;
const COMBO_TIME_LIMIT = 5;